import { useEffect, useState } from "react";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Settings2, ChevronLeft, Save, Star, Radio } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function AdminSiteSettings() {
  const [stations, setStations] = useState([]);
  const [settings, setSettings] = useState({
    stationOfMonth: "",
    featuredStations: [],
    platformBanner: "",
    bannerActive: false,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [settingsDoc, stationsSnap] = await Promise.all([
        getDoc(doc(db, "siteSettings", "global")),
        getDocs(collection(db, "stations")),
      ]);

      const stationsData = stationsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setStations(stationsData);

      if (settingsDoc.exists()) {
        setSettings((s) => ({ ...s, ...settingsDoc.data() }));
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "siteSettings", "global"), settings, { merge: true });
      toast.success("Site settings saved!");
    } catch (e) {
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  const toggleFeaturedStation = (id) => {
    setSettings((s) => {
      const current = s.featuredStations || [];
      if (current.includes(id)) return { ...s, featuredStations: current.filter((x) => x !== id) };
      if (current.length >= 3) { toast.error("Max 3 featured stations"); return s; }
      return { ...s, featuredStations: [...current, id] };
    });
  };

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#00ff77] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-site-settings" className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1"><ChevronLeft size={16} /> Admin</Link>
        <h2 className="text-xl font-black text-white flex items-center gap-2">
          <Settings2 size={18} className="text-[#00ff77]" /> Site Settings
        </h2>
      </div>

      {/* Station of the Month */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Star size={16} className="text-yellow-400" />
          <h3 className="text-white font-bold text-sm">Station of the Month</h3>
          <span className="text-zinc-600 text-xs">— shown in the Radio page hero</span>
        </div>
        <select
          value={settings.stationOfMonth}
          onChange={(e) => setSettings((s) => ({ ...s, stationOfMonth: e.target.value }))}
          data-testid="station-of-month-select"
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00ff77]/50"
        >
          <option value="">— None selected —</option>
          {stations.map((st) => (
            <option key={st.id} value={st.id}>{st.name}</option>
          ))}
        </select>
      </div>

      {/* Featured Stations (up to 3) */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Radio size={16} className="text-[#B026FF]" />
          <h3 className="text-white font-bold text-sm">Featured Stations</h3>
          <span className="text-zinc-600 text-xs">— up to 3, shown on Radio page</span>
        </div>
        <div className="space-y-2">
          {stations.map((st) => {
            const isFeatured = (settings.featuredStations || []).includes(st.id);
            return (
              <label key={st.id} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={() => toggleFeaturedStation(st.id)}
                  className="accent-[#B026FF]"
                />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {st.logoURL && <img src={st.logoURL} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />}
                  <span className={`text-sm truncate ${isFeatured ? "text-white font-medium" : "text-zinc-400"}`}>{st.name}</span>
                  {st.genre && <span className="text-[10px] text-zinc-600 bg-white/5 px-1.5 rounded-full">{st.genre}</span>}
                </div>
              </label>
            );
          })}
          {stations.length === 0 && <p className="text-zinc-600 text-sm">No stations yet.</p>}
        </div>
      </div>

      {/* Platform Banner */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold text-sm">Platform Banner</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-zinc-500 text-xs">Active</span>
            <input
              type="checkbox"
              checked={settings.bannerActive || false}
              onChange={(e) => setSettings((s) => ({ ...s, bannerActive: e.target.checked }))}
              className="accent-[#00ff77]"
            />
          </label>
        </div>
        <textarea
          value={settings.platformBanner || ""}
          onChange={(e) => setSettings((s) => ({ ...s, platformBanner: e.target.value }))}
          rows={2}
          placeholder="e.g. Beta launch weekend — all content free! 🎉"
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00ff77]/50 resize-none"
        />
        <p className="text-zinc-600 text-xs">Shown as a banner on the homepage when Active is checked.</p>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="save-site-settings-btn"
        className="flex items-center gap-2 bg-[#00ff77] text-black font-bold text-sm px-6 py-2.5 rounded-full hover:bg-[#00e66b] transition-colors disabled:opacity-50"
      >
        <Save size={14} /> {saving ? "Saving..." : "Save Settings"}
      </button>
    </div>
  );
}
